import { Injectable } from '@angular/core';
import { Sort } from '@angular/material/sort';

import { BehaviorSubject, Observable } from 'rxjs';

import { SafeAny } from '../../types/safe-any';

@Injectable({
  providedIn: 'root',
})
export class FiltersAndPaginationPayloadService {
  private readonly defaultPayload: SafeAny = {
    offset: 1,
    limit: 10,
  };

  private filterPayload = new BehaviorSubject<SafeAny>({
    client: null,
    status: null,
    assignee: null,
  });

  private myQuotesFilterPayload = new BehaviorSubject<SafeAny>({
    status: null,
    client: null,
  });

  private paginationPayload = new BehaviorSubject<SafeAny>(this.defaultPayload);
  private sortPayload = new BehaviorSubject<string | null>(null);
  private orderSearchPayload = new BehaviorSubject<string>('');
  private quotesSearchPayload = new BehaviorSubject<string>('');

  // Getters as Observables
  get filterPayload$(): Observable<SafeAny> {
    return this.filterPayload.asObservable();
  }

  get paginationPayload$(): Observable<SafeAny> {
    return this.paginationPayload.asObservable();
  }

  get sortPayload$(): Observable<string | null> {
    return this.sortPayload.asObservable();
  }

  get myQuotesFilterPayload$(): Observable<SafeAny> {
    return this.myQuotesFilterPayload.asObservable();
  }

  get orderSearchPayload$(): Observable<string> {
    return this.orderSearchPayload.asObservable();
  }

  get quotesSearchPayload$(): Observable<string> {
    return this.quotesSearchPayload.asObservable();
  }

  // Current value getters
  get currentFilterPayload(): SafeAny {
    return this.filterPayload.getValue();
  }

  get currentPaginationPayload(): SafeAny {
    return this.paginationPayload.getValue();
  }

  get currentSort(): string | null {
    return this.sortPayload.getValue();
  }

  get currentMyQuotesFilterPayload(): SafeAny {
    return this.myQuotesFilterPayload.getValue();
  }

  get currentOrderSearchPayload(): string {
    return this.orderSearchPayload.getValue();
  }

  get currentQuotesSearchPayload(): string {
    return this.quotesSearchPayload.getValue();
  }

  // Combined payload getter
  get combinedPayload(): SafeAny {
    return {
      ...this.currentPaginationPayload,
      ...this.currentFilterPayload,
      ...(this.currentSort && { sort: this.currentSort }),
    };
  }

  // Setters
  updateFilterPayload(payload: SafeAny): void {
    this.filterPayload.next({
      ...this.currentFilterPayload,
      ...payload,
    });
  }

  updatePaginationPayload(payload: SafeAny): void {
    this.paginationPayload.next({
      ...this.currentPaginationPayload,
      ...payload,
    });
  }

  updateSort(sortValue: string | null): void {
    this.sortPayload.next(sortValue);
  }

  // Helper method to handle sort updates from Material Sort
  updateSortFromMaterial(sort: Sort): void {
    const formattedSort = sort.direction === 'desc' ? `-${sort.active}` : sort.active;
    this.updateSort(formattedSort);
  }

  updateMyQuotesFilterPayload(payload: SafeAny): void {
    this.myQuotesFilterPayload.next({
      ...this.currentMyQuotesFilterPayload,
      ...payload,
    });
  }

  updateOrderSearchPayload(searchValue: string): void {
    this.orderSearchPayload.next(searchValue);
  }

  updateQuotesSearchPayload(searchValue: string): void {
    this.quotesSearchPayload.next(searchValue);
  }

  // Reset methods
  resetFilters(): void {
    this.filterPayload.next({
      client: null,
      status: null,
      assignee: null,
    });
  }

  resetMyQuotesFilters(): void {
    this.myQuotesFilterPayload.next({
      status: null,
      client: null,
      archived: false,
      limit: 10,
      offset: 1,
    });
  }

  resetPagination(): void {
    this.paginationPayload.next(this.defaultPayload);
  }

  resetSort(): void {
    this.sortPayload.next(null);
  }

  resetAll(): void {
    this.resetFilters();
    this.resetMyQuotesFilters();
    this.resetPagination();
  }
}
